export interface SectorBucket {
  /** URL-safe identifier, used in ?sector= param and API query param */
  value: string;
  /** Human-readable label for the UI */
  label: string;
  /** Free-text values from articles.industries[] that map to this bucket.
   *  Empty for the "all" bucket — it matches everything. */
  industries: string[];
}

export const SECTOR_BUCKETS: SectorBucket[] = [
  { value: "all", label: "All sectors", industries: [] },
  {
    value: "finance",
    label: "Finance",
    industries: [
      "finance",
      "banking",
      "financial-services",
      "financial services",
      "insurance",
    ],
  },
  {
    value: "government",
    label: "Government",
    industries: [
      "government",
      "gov",
      "public-sector",
      "public sector",
      "municipal",
    ],
  },
  {
    value: "healthcare",
    label: "Healthcare",
    industries: [
      "healthcare",
      "health",
      "medical",
      "pharma",
      "pharmaceutical",
    ],
  },
  {
    value: "critical-infrastructure",
    label: "Critical Infrastructure",
    industries: [
      "critical infrastructure",
      "critical-infrastructure",
      "critical-infra",
      "utilities",
      "energy",
      "water",
      "transportation",
    ],
  },
  {
    value: "cybersecurity",
    label: "Cybersecurity / Software",
    industries: [
      "cybersecurity",
      "cyber security",
      "software",
      "technology",
      "tech",
      "saas",
      "cloud",
    ],
  },
  {
    value: "defense",
    label: "Defense",
    industries: [
      "defense",
      "defence",
      "military",
      "aerospace",
      "defense-industrial",
    ],
  },
  {
    value: "retail",
    label: "Retail / E-commerce",
    industries: [
      "retail",
      "e-commerce",
      "ecommerce",
      "consumer",
      "hospitality",
    ],
  },
  {
    value: "education",
    label: "Education",
    industries: ["education", "academia", "university", "school"],
  },
  {
    value: "telecom",
    label: "Telecommunications",
    industries: ["telecom", "telecommunications", "isp", "carrier"],
  },
  {
    value: "other",
    label: "Other",
    industries: [], // catch-all — assigned by sectorForIndustry when no bucket matches
  },
];

/**
 * Given a free-text industry string (as stored in articles.industries[]),
 * return the canonical SECTOR_BUCKETS value it belongs to.
 * Falls back to "other" when no match is found.
 * Case-insensitive and trims whitespace.
 */
export function sectorForIndustry(industry: string): string {
  const normalised = industry.toLowerCase().trim();
  for (const bucket of SECTOR_BUCKETS) {
    if (bucket.value === "all" || bucket.value === "other") continue;
    if (bucket.industries.includes(normalised)) return bucket.value;
  }
  return "other";
}

/** Display label for a sector value. Returns the value itself as fallback. */
export function sectorLabel(value: string): string {
  return SECTOR_BUCKETS.find((b) => b.value === value)?.label ?? value;
}
